import { API } from '@/modules/api'
import type { TCorePowerschoolFeeCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { useCartStore } from '@/stores/cart'
import { hasUnsignedForms } from '@/mixins/OtherFeeHelper'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'

export const createUniqueId = (feeId: string) => `${feeId}::${OrderItemTypeCode.CorePowerschoolFee}`

export const useCorePowerschoolFeeStore = defineStore('corePowerschoolFee', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    coreFees: {} as Record<TStudentAccountId, TCorePowerschoolFeeCartLine[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    getCoreFees(state) {
      return (studentAccountId: TStudentAccountId) => state.coreFees[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const coreFees = await API.Get.corePowerschoolFees(studentAccountId)
      this.coreFees[studentAccountId] = coreFees
      this.validateCart(districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the core powerschool fees store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId) as TCorePowerschoolFeeCartLine[]) {
        if (
          line.orderItemType === OrderItemType.CorePowerschoolFee &&
          line.studentAccountId === studentAccountId
        ) {
          const fee = this.coreFees[studentAccountId].find(fee => fee.feeId === line.feeId)
          if (!fee) {
            console.log('core powerschool fee not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          // DTA-659: the add-time gate was hardened in CorePowerschoolFeeSection, but
          // the cart is persisted and rehydrated verbatim, so a line stored before the
          // fix — or before the item required a form — never meets that gate again.
          // Unlike SFS there is no server-side form check for this order type, so this
          // is the last place the line can be caught.
          if (hasUnsignedForms(fee)) {
            console.log('core powerschool fee now requires an unsubmitted form', line.feeId)
            this.removeFromCart(districtId, line.feeId)
            continue
          }
          // ensure fee amount is less than cart amount meaning cart amount is too high
          if (fee.amountDue < line.amount) {
            console.log(
              'core powerschool fee amount is less than cart amount, adjusting cart',
              line.feeId
            )
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fee.amountDue
            })
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, feeData: Omit<TCorePowerschoolFeeCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, { ...feeData, uniqueId })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TCorePowerschoolFeeCartLine> & { feeId: string }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: string) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.CorePowerschoolFee) {
          this.removeFromCart(districtId, (line as TCorePowerschoolFeeCartLine).feeId)
        }
      }
    }
  }
})
